/**
 * FEAT-023 — the settings registry.
 *
 * BR-080: **every setting ships a default in code.** A row in the `setting`
 * table is an override, so a missing or malformed row can never prevent
 * start-up — the default applies and the condition is logged. That is why this
 * catalogue lives here rather than in the database: the database is where
 * administrators express exceptions, not where the platform learns what its own
 * settings are.
 *
 * A key that is not in this catalogue is rejected with `unknown_setting_key`
 * (REQ-137), so a typo cannot quietly create a setting nothing reads.
 */

/**
 * The types FRS §5.9 defines, matching the `setting_type` database enum exactly.
 *
 * Richer than a bare string/number/boolean because the Administration screen
 * renders the right control from it: `timezone` gets a picker, `secret` gets a
 * write-only field, `url` gets validation. An invented parallel type list here
 * would have to be mapped onto the enum somewhere, and that mapping is a thing
 * that can be wrong.
 */
export type SettingType =
  'string' | 'integer' | 'boolean' | 'enum' | 'email' | 'url' | 'timezone' | 'secret' | 'file_ref';

export interface SettingDefinition {
  key: string;
  type: SettingType;
  /** Applied whenever there is no override. Never null for a non-sensitive key. */
  default: string | number | boolean | null;
  /**
   * Credential-bearing. Reads return a masked "is set" indicator, writes go to
   * the secret store, and the audit entry records a redaction marker rather than
   * the old and new values (REQ-128, BR-082).
   */
  sensitive?: boolean;
  /** Shown beside the field on the Administration screen. */
  description: string;
}

export const SETTING_DEFINITIONS: readonly SettingDefinition[] = [
  {
    key: 'platform.name',
    type: 'string',
    default: 'Boud',
    description: 'Shown in the sidebar and in outbound message subjects.',
  },
  {
    key: 'locale.default_language',
    type: 'string',
    default: 'en',
    description: 'The language a new employee starts with before they choose.',
  },
  {
    key: 'locale.default_timezone',
    type: 'timezone',
    // ASM-01. Changing it would move every booking's day boundary, which is why
    // it is a setting rather than a constant — but it is not a casual edit.
    default: 'Asia/Riyadh',
    description: 'The building’s timezone. Determines when "today" begins.',
  },
  {
    key: 'notifications.email_enabled',
    type: 'boolean',
    default: true,
    description: 'Send email notifications. In-app notifications are unaffected.',
  },
  {
    key: 'notifications.slack_enabled',
    type: 'boolean',
    /*
     * True, but inert until a bot token is configured — the same rule the
     * calendar providers use: a capability with no credential is simply not
     * offered, rather than hidden behind a second feature flag that can
     * disagree with it. This exists so an administrator can silence Slack
     * without deleting the token they would have to re-issue to get it back.
     */
    default: true,
    description: 'Send notifications to Slack as well as by email. Needs a bot token below.',
  },
  {
    key: 'notifications.max_attempts',
    type: 'integer',
    // Bounded retry (FEAT-022). Without a ceiling, a permanently-rejected
    // address is retried forever and the queue never drains.
    default: 5,
    description: 'How many times a failed delivery is retried before it is abandoned.',
  },
  {
    key: 'email.from_address',
    type: 'email',
    default: 'no-reply@boud.ai',
    description: 'The From address on outbound mail. Must pass SPF and DKIM.',
  },
  {
    /*
     * Deliberately not named after a vendor. This said "The Google Workspace
     * SMTP relay host" while pointing at Mailgun, which is the description an
     * administrator reads *while deciding what to type into it*.
     */
    key: 'email.smtp_host',
    type: 'string',
    default: 'smtp-relay.gmail.com',
    description: 'SMTP relay hostname. Any relay that speaks STARTTLS on the port below.',
  },
  {
    key: 'email.smtp_port',
    type: 'integer',
    default: 587,
    description: 'SMTP port. 587 with STARTTLS suits every common relay.',
  },
  {
    key: 'email.smtp_username',
    type: 'string',
    default: '',
    description: 'Relay username, if the relay requires authentication.',
  },
  {
    key: 'email.smtp_password',
    type: 'secret',
    default: null,
    sensitive: true,
    description: 'Relay password. Stored write-only and never returned.',
  },
  /*
   * Both calendar providers are first-class. Occupants of the building are not
   * all on the same platform — Boud runs Workspace, a tenant company may run
   * Microsoft 365 — and REQ-132 gives each *user* one active provider, so the
   * platform has to be able to offer either.
   *
   * A provider with no client id configured is simply not offered; that is how
   * "available, disabled by default" (§22.5) is expressed, rather than with a
   * feature flag.
   */
  {
    key: 'calendar.google.client_id',
    type: 'string',
    default: '',
    description: 'OAuth client id for Google Calendar. Leave empty to hide the option.',
  },
  {
    key: 'calendar.google.client_secret',
    type: 'secret',
    default: null,
    sensitive: true,
    description: 'Google OAuth client secret. Stored write-only and never returned.',
  },
  {
    key: 'calendar.microsoft.client_id',
    type: 'string',
    default: '',
    description: 'Entra application id for Outlook Calendar. Leave empty to hide the option.',
  },
  {
    key: 'calendar.microsoft.client_secret',
    type: 'secret',
    default: null,
    sensitive: true,
    description: 'Entra client secret. Stored write-only and never returned.',
  },
  {
    key: 'calendar.microsoft.tenant_id',
    type: 'string',
    // `organizations` lets any work or school account consent, which is what a
    // multi-tenant building needs. A single tenant id restricts it to one.
    default: 'organizations',
    description: 'Entra tenant. "organizations" allows any work or school account.',
  },
  {
    key: 'calendar.sweep_seconds',
    type: 'integer',
    /*
     * Two minutes. Cheap — four rooms at this interval is under 3,000 Google
     * calls a day against a quota of a million — and until push subscriptions
     * are registered this interval *is* the latency of inbound sync, not a
     * backstop beside it.
     */
    default: 120,
    description: 'How often each room’s calendar is re-read. Lower means fresher, and more calls.',
  },

  /*
   * Everything below moved out of the process environment on 2026-08-05, so an
   * administrator can change it without a deploy.
   *
   * What deliberately did **not** move, and must not:
   *
   *   - `DATABASE_URL`. Settings live in the database; it cannot configure
   *     itself.
   *   - `JWT_SECRET`. It signs session tokens. Behind `Settings.Manage` it would
   *     let anybody holding that capability set the key that forges any session,
   *     including Super Admin's — turning an administrative capability into
   *     account takeover. It belongs in Secret Manager.
   *   - `ROLE`. It decides whether a process drains the queues. Flipped on the
   *     API instances, every autoscaled instance would drain the same queue.
   *   - `CORS_ORIGINS`, `COOKIE_SECURE`, ports and paths. Deployment wiring; a
   *     wrong value here locks everybody out of the screen that would fix it.
   */
  {
    key: 'platform.web_base_url',
    type: 'url',
    default: 'http://localhost:5173',
    description: 'Where the web app is reachable. Used to build links in outbound email.',
  },
  {
    key: 'platform.api_public_url',
    type: 'url',
    default: 'http://localhost:3000',
    /*
     * An OAuth redirect URI must match what was registered with Google or
     * Microsoft exactly — scheme, host, port and path. It is a setting rather
     * than something derived from the request because deriving it would let a
     * Host header decide where an authorisation code is delivered.
     */
    description:
      'Where this API is reachable from a browser. Must match the OAuth redirect URI exactly.',
  },
  {
    key: 'security.login_max_attempts',
    type: 'integer',
    default: 5,
    description: 'Failed sign-ins before an account is locked, with backoff doubling after each.',
  },
  {
    key: 'security.login_lock_seconds',
    type: 'integer',
    default: 900,
    description: 'How long the first lockout lasts. Each further failure doubles it.',
  },
  {
    key: 'security.reset_token_ttl_minutes',
    type: 'integer',
    default: 60,
    description: 'How long a password-reset link stays valid. It is single-use regardless.',
  },
  /*
   * Slack (FEAT-014, 2026-08-05).
   *
   * One credential and nothing else. Which Slack account a notification reaches
   * is resolved from the recipient's *email address* through Slack's own
   * directory, so there is no per-employee Slack id to store, no screen for
   * entering one, and nobody to chase when somebody's id is missing or stale.
   *
   * The consequence — that a person whose Slack address differs from their Boud
   * address is simply not found — is the right failure: they keep receiving
   * email, which is the floor for everybody regardless.
   */
  {
    key: 'slack.bot_token',
    type: 'secret',
    default: null,
    sensitive: true,
    /*
     * A bot token (`xoxb-`), not a user token. It needs `chat:write` to post and
     * `users:read.email` to turn an address into a Slack user. Both are the
     * narrowest scopes that do the job: neither can read a message.
     */
    description:
      'Slack bot token (xoxb-…), with chat:write and users:read.email. Leave empty to disable Slack.',
  },
  {
    key: 'attachments.max_count',
    type: 'integer',
    default: 10,
    description: 'How many files one ticket may carry.',
  },
  {
    key: 'attachments.max_bytes',
    type: 'integer',
    default: 10 * 1024 * 1024,
    description: 'Largest single attachment. Cannot exceed the process upload ceiling.',
  },
  {
    key: 'attachments.total_max_bytes',
    type: 'integer',
    /*
     * FEAT-017 needs both this and the per-file limit: without an aggregate, a
     * caller attaches a thousand files that each pass the per-file check.
     */
    default: 25 * 1024 * 1024,
    description: 'Largest total across every attachment on one ticket.',
  },
  {
    key: 'attachments.download_ttl_seconds',
    type: 'integer',
    default: 300,
    description: 'How long a download link stays valid. It carries no session of its own.',
  },
] as const;

const BY_KEY = new Map(SETTING_DEFINITIONS.map((d) => [d.key, d]));

export function settingDefinition(key: string): SettingDefinition | undefined {
  return BY_KEY.get(key);
}

export function isSettingKey(key: string): boolean {
  return BY_KEY.has(key);
}

/** The keys whose values must never leave the server (BR-082). */
export const SENSITIVE_SETTING_KEYS: readonly string[] = SETTING_DEFINITIONS.filter(
  (d) => d.sensitive,
).map((d) => d.key);

/**
 * The capabilities a provider fulfils.
 *
 * BR-074 — a module names a *capability*, never a provider. `calendar` is the
 * contract; `google` is one implementation of it. This is the list the
 * integration registry is keyed on.
 */
export const INTEGRATION_CAPABILITIES = ['calendar', 'email'] as const;
export type IntegrationCapability = (typeof INTEGRATION_CAPABILITIES)[number];
